import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { layoutNucleus } from '../lib/nucleus';
import { orbitView, perspective } from '../lib/glmat';
import type { GenomeBuild } from '../lib/cytobands';
import type { ParsedGenome } from '../types';
import { Explain } from './Explain';

interface Props {
  genome: ParsedGenome;
}

/*
 * Raw WebGL rather than three.js. three is ~600 kB gzipped and would more than
 * double this app's bundle for one view; everything needed here is a single
 * gl.POINTS draw call with three attributes. No dependency also means nothing
 * new to audit against the privacy model.
 */

const VERT = `#version 300 es
// GLSL ES 3.00 defaults vertex floats to highp but gives fragment shaders no
// default at all. Any uniform shared by both must be declared at the SAME
// precision in each or linking fails, so both files state it explicitly.
precision highp float;

in vec3 aPos;
in vec3 aColor;
in float aSize;

uniform mat4 uProj;
uniform mat4 uView;
uniform float uPointScale;
uniform float uMarkerOnly;
uniform float uIsLine;

out vec3 vColor;
out float vFade;

void main() {
  vec4 eye = uView * vec4(aPos, 1.0);
  gl_Position = uProj * eye;

  // Perspective point sizing: nearer points get bigger, so flying in actually
  // resolves individual variants instead of everything staying a uniform speck.
  float dist = max(0.1, -eye.z);
  gl_PointSize = clamp((aSize * uPointScale) / dist, 0.75, 40.0);

  vColor = aColor;
  // Dim the far side of the nucleus so the near surface reads as nearer;
  // without this a point cloud looks flat however you rotate it.
  vFade = clamp(1.35 - dist * 0.055, 0.18, 1.0);

  // "Markers only" hides ordinary variants by collapsing them to a degenerate
  // point rather than rebuilding the buffers.
  if (uMarkerOnly > 0.5 && uIsLine < 0.5 && aSize < 2.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  }
}`;

const FRAG = `#version 300 es
precision highp float;

in vec3 vColor;
in float vFade;
out vec4 outColor;

uniform float uIsLine;

void main() {
  // gl_PointCoord is only defined while rasterising POINTS — reading it for a
  // line is undefined behaviour, so the backbone pass skips the round mask.
  if (uIsLine > 0.5) {
    outColor = vec4(vColor * vFade, vFade);
    return;
  }
  vec2 d = gl_PointCoord - vec2(0.5);
  float r = dot(d, d);
  if (r > 0.25) discard;
  float alpha = smoothstep(0.25, 0.06, r);
  outColor = vec4(vColor * vFade, alpha * vFade);
}`;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`shader compile failed: ${log}`);
  }
  return sh;
}

export function Nucleus3D({ genome }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [build] = useState<GenomeBuild>(() =>
    genome.meta.assembly ?? (genome.format === 'vcf' ? 'hg38' : 'hg19'),
  );
  const [markerOnly, setMarkerOnly] = useState(false);
  const [spin, setSpin] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const layout = useMemo(() => layoutNucleus(genome, { build }), [genome, build]);

  // Camera state lives in a ref: it changes every frame and must never trigger
  // a React render.
  const cam = useRef({ yaw: 0.6, pitch: 0.35, dist: 0 });
  if (cam.current.dist === 0) cam.current.dist = layout.extent * 1.9;

  const spinRef = useRef(spin);
  spinRef.current = spin;
  const markerRef = useRef(markerOnly);
  markerRef.current = markerOnly;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl2', {
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
    });
    if (!gl) {
      setError('WebGL2 is not available in this browser, so the 3D view cannot render.');
      return;
    }

    let program: WebGLProgram | null = null;
    let raf = 0;
    let disposed = false;

    try {
      program = gl.createProgram()!;
      const vs = compile(gl, gl.VERTEX_SHADER, VERT);
      const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
      gl.attachShader(program, vs);
      gl.attachShader(program, fs);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(`link failed: ${gl.getProgramInfoLog(program)}`);
      }
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to initialise WebGL');
      return;
    }

    gl.useProgram(program);

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);

    const bind = (data: Float32Array, name: string, size: number) => {
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(program!, name);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
      return buf;
    };
    const buffers = [
      bind(layout.positions, 'aPos', 3),
      bind(layout.colors, 'aColor', 3),
      bind(layout.sizes, 'aSize', 1),
    ];

    // Backbone gets its own VAO. aSize is unused for lines but the attribute
    // must still be supplied or the draw reads whatever was last bound.
    const lineVao = gl.createVertexArray();
    gl.bindVertexArray(lineVao);
    buffers.push(
      bind(layout.backbone, 'aPos', 3),
      bind(layout.backboneColors, 'aColor', 3),
      bind(new Float32Array(layout.backboneCount), 'aSize', 1),
    );
    gl.bindVertexArray(null);

    const uProj = gl.getUniformLocation(program, 'uProj');
    const uView = gl.getUniformLocation(program, 'uView');
    const uPointScale = gl.getUniformLocation(program, 'uPointScale');
    const uMarkerOnly = gl.getUniformLocation(program, 'uMarkerOnly');
    const uIsLine = gl.getUniformLocation(program, 'uIsLine');

    gl.enable(gl.BLEND);
    // Additive blending: overlapping points accumulate, so dense regions glow.
    // That is the honest readout for a point cloud — brightness means density.
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.disable(gl.DEPTH_TEST);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      return canvas.width / Math.max(1, canvas.height);
    };

    let last = 0;
    const frame = (now: number) => {
      if (disposed) return;
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      if (spinRef.current) cam.current.yaw += dt * 0.18;

      const aspect = resize();
      const proj = perspective((50 * Math.PI) / 180, aspect, 0.1, 500);
      const view = orbitView(cam.current.yaw, cam.current.pitch, cam.current.dist);

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindVertexArray(vao);
      gl.uniformMatrix4fv(uProj, false, proj);
      gl.uniformMatrix4fv(uView, false, view);
      gl.uniform1f(uPointScale, canvas.height * 0.22);
      gl.uniform1f(uMarkerOnly, markerRef.current ? 1 : 0);

      // Backbone first, so data points composite on top of the scaffolding.
      gl.bindVertexArray(lineVao);
      gl.uniform1f(uIsLine, 1);
      gl.drawArrays(gl.LINES, 0, layout.backboneCount);

      gl.bindVertexArray(vao);
      gl.uniform1f(uIsLine, 0);
      gl.drawArrays(gl.POINTS, 0, layout.count);

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      buffers.forEach((b) => gl.deleteBuffer(b));
      gl.deleteVertexArray(vao);
      gl.deleteVertexArray(lineVao);
      if (program) gl.deleteProgram(program);
    };
  }, [layout]);

  // ── pointer interaction ────────────────────────────────────────────────────
  const drag = useRef<{ x: number; y: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    drag.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
    setSpin(false);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    cam.current.yaw += dx * 0.006;
    // Stop just short of the poles, where the up-vector degenerates.
    const lim = Math.PI / 2 - 0.05;
    cam.current.pitch = Math.max(-lim, Math.min(lim, cam.current.pitch + dy * 0.006));
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      const min = layout.extent * 0.06; // close enough to sit inside a coil
      const max = layout.extent * 6;
      cam.current.dist = Math.max(
        min,
        Math.min(max, cam.current.dist * Math.exp(e.deltaY * 0.0012)),
      );
    },
    [layout.extent],
  );

  return (
    <div className="card">
      <h2>
        Nucleus <span className="muted">· {layout.count.toLocaleString()} variants in 3D · {build}</span>
        <Explain id="nucleus" />
      </h2>

      <div className="controls">
        <label className="inline-control">
          <input type="checkbox" checked={spin} onChange={(e) => setSpin(e.target.checked)} />
          Rotate
        </label>
        <label className="inline-control">
          <input
            type="checkbox"
            checked={markerOnly}
            onChange={(e) => setMarkerOnly(e.target.checked)}
          />
          Condition markers only
        </label>
        <button
          className="ghost"
          onClick={() => {
            cam.current = { yaw: 0.6, pitch: 0.35, dist: layout.extent * 1.9 };
          }}
        >
          Reset view
        </button>
      </div>

      {error ? (
        <div className="note" role="alert">{error}</div>
      ) : (
        <div className="nucleus-stage">
          <canvas
            ref={canvasRef}
            className="nucleus-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            aria-label={`Three-dimensional point cloud of ${layout.count.toLocaleString()} variants arranged by chromosome`}
          />
          <div className="nucleus-hint">drag to orbit · scroll to fly in</div>
        </div>
      )}

      <div className="nucleus-legend">
        {layout.perChrom.map((c) => (
          <span key={c.chrom} className="nucleus-key" title={`${c.count.toLocaleString()} variants`}>
            <i
              className="swatch"
              style={{
                background: `rgb(${c.color.map((v) => Math.round(v * 255)).join(',')})`,
              }}
            />
            {c.chrom}
          </span>
        ))}
        <span className="nucleus-key">
          <i className="swatch" style={{ background: 'rgb(255,247,217)', borderRadius: '50%' }} />
          condition marker
        </span>
      </div>

      <div className="note info" style={{ marginTop: 14 }}>
        Each chromosome is drawn as its own coil radiating from the centre, because
        interphase chromosomes really do occupy separate territories rather than mixing
        freely. Brightness is density — overlapping points add up, so the glowing stretches
        are where your test sampled most heavily.
        {layout.skipped > 0 &&
          ` ${layout.skipped.toLocaleString()} variants on unplaced contigs are not shown.`}
      </div>
    </div>
  );
}
